import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { Button } from '@gafe/common-ui';

import { useOrderContext } from '@/components/modals/SubmitOrder/helpers';
import { ActivityInstance, CalculationMethod } from '@/controllers/Activity';
import { useBomTotal } from '@/hooks';
import { AbilityContext } from '@/hooks/ability/Can';
import { ROUTES } from '@/router';

import { getOrderTotal } from '../../constants';
import {
  getNonEquipmentItems,
  separateNonEquipmentItems,
  TIMBERLINE_PRODUCT_NAME,
} from '../ConfirmationStep/helpers';

import { SuccessContractDetails } from './components/SuccessContractDetails';
import { SuccessOrderDetails } from './components/SuccessOrderDetails';
import { SuccessProposalDetails } from './components/SuccessProposalDetails/SuccessProposalDetails';
import { WhatsNext } from './components/WhatsNext';
import { Props } from './types';

import styles from './SuccessStep.module.scss';

export const SuccessStep = ({
  proposal,
  calculatedTax,
  homeownerAddress,
  productLineName,
  createdOrderId,
  close,
}: Props) => {
  const ability = useAbility(AbilityContext);
  const history = useHistory();
  const state = useOrderContext();
  const isActivityDistributionModel = state.isDistribution;
  const bomTotal = useBomTotal(state, proposal);

  const skuEnable =
    ability.can('view', 'SKU_PRICING') &&
    !ability.can('view', 'Bulk Fulfillment Program') &&
    proposal.priceCalculationMethod === CalculationMethod.SKU;

  const nonEquipmentItems = getNonEquipmentItems(state);
  const proposalAdders = proposal?.adders.map(proposalAdder => proposalAdder.name) ?? [];
  const { servicesTotal, addersTotal } = separateNonEquipmentItems(
    state,
    nonEquipmentItems,
    proposalAdders,
  );

  let orderTotal: number;

  if (isActivityDistributionModel) {
    orderTotal = servicesTotal + addersTotal;
  } else {
    orderTotal = skuEnable ? bomTotal : getOrderTotal(state, productLineName, proposal);
  }

  const isTimberline = productLineName === TIMBERLINE_PRODUCT_NAME;
  const totalPrice = orderTotal + (calculatedTax ?? 0);

  const navigateToOrderDetails = useCallback(() => {
    if (!createdOrderId) return;

    close();

    history.push(
      `${ROUTES.Private.Dashboard.ActivityList}/${ActivityInstance.OrderWithoutProject}-${createdOrderId}`,
    );
  }, [close, createdOrderId, history]);

  const closeModalWithReturnToDashboard = useCallback(() => {
    close();

    if (createdOrderId) {
      history.push(ROUTES.Private.Dashboard.ActivityList);
    }
  }, [close, createdOrderId, history]);

  return (
    <div className={styles.success_page_wrapper}>
      <div className={styles.success_page}>
        <span className={styles.title}>
          {state.isAccessProgram
            ? 'Thank you for requesting your contract'
            : 'Thanks for submitting your order'}
        </span>
        {state.isAccessProgram && !state.isDistribution && (
          <span className={styles.subtitle}>
            The contract will be sent to the homeowner shortly.
          </span>
        )}
        <SuccessProposalDetails
          homeownerAddress={homeownerAddress}
          proposal={proposal}
          totalPrice={totalPrice}
        />
        <div className={styles.products_info_block}>
          {!isActivityDistributionModel && (
            <span className={styles.shipment_title}>Here’s what is included in your shipment:</span>
          )}
          <SuccessContractDetails />
          <SuccessOrderDetails isTimberline={isTimberline} productLineName={productLineName} />
          <WhatsNext isTimberline={isTimberline} navitageTo={navigateToOrderDetails} />
        </div>
      </div>
      <div className={styles.stepper_buttons}>
        <Button
          colorSchema="neutral"
          onClick={closeModalWithReturnToDashboard}
          size="large"
          variant="text"
        >
          Close
        </Button>
      </div>
    </div>
  );
};
